import { useState, useCallback, useRef, useEffect } from 'react';

// Единый отступ от краёв viewport (соответствует MARGIN в widgetGeometry.js)
const DRAG_MARGIN = 16;

/**
 * Custom hook для перетаскивания элемента
 * Поддерживает как mouse events (desktop), так и touch events (mobile)
 *
 * @param {Object} options - Опции для dragging
 * @param {Object} options.initialPosition - Начальная позиция { x, y }
 * @param {Object} options.elementSize - Размер элемента { width, height } для расчёта границ
 * @param {Function} options.onDragEnd - Callback при завершении перетаскивания
 * @param {boolean} options.disabled - Отключить dragging
 * @returns {Object} { position, handleMouseDown, handleTouchStart, isDragging, resetPosition }
 */
export const useDraggable = ({
  initialPosition = { x: 20, y: 20 },
  elementSize = { width: 300, height: 56 },
  onDragEnd,
  disabled = false
} = {}) => {
  const [position, setPosition] = useState(initialPosition);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const elementPositionRef = useRef({ x: 0, y: 0 });
  // Размер для границ, замеренный в начале drag'а. `elementSize` — номинал
  // (300px бар), но реальный элемент бывает шире: с WhatsApp + Telegram бар
  // растёт на слот (#1493), и по номиналу его можно было утащить за правый
  // край. Берём максимум из номинала и замера: drag-хендл чата — шапка, её
  // высота меньше окна, и замер не должен ужимать границу.
  const dragSizeRef = useRef(elementSize);

  // Update position when initialPosition changes (e.g., from localStorage)
  useEffect(() => {
    setPosition(initialPosition);
  }, [initialPosition.x, initialPosition.y]);

  // Shared move handler for both mouse and touch
  const handleMove = useCallback((clientX, clientY) => {
    if (!isDragging) return;

    const deltaX = clientX - dragStartRef.current.x;
    const deltaY = clientY - dragStartRef.current.y;

    const newX = elementPositionRef.current.x + deltaX;
    const newY = elementPositionRef.current.y + deltaY;

    // Boundary detection - единый отступ DRAG_MARGIN (16px) от всех краёв viewport
    const minX = DRAG_MARGIN;
    const minY = DRAG_MARGIN;
    const maxX = window.innerWidth - dragSizeRef.current.width - DRAG_MARGIN;
    const maxY = window.innerHeight - dragSizeRef.current.height - DRAG_MARGIN;

    const boundedX = Math.max(minX, Math.min(newX, maxX));
    const boundedY = Math.max(minY, Math.min(newY, maxY));

    setPosition({ x: boundedX, y: boundedY });
  }, [isDragging]);

  const measureDragSize = useCallback((target) => {
    const rect = target?.getBoundingClientRect?.();
    dragSizeRef.current = {
      width: Math.max(elementSize.width, rect?.width || 0),
      height: Math.max(elementSize.height, rect?.height || 0),
    };
  }, [elementSize.width, elementSize.height]);

  const handleMouseMove = useCallback((e) => {
    handleMove(e.clientX, e.clientY);
  }, [handleMove]);

  const handleTouchMove = useCallback((e) => {
    if (e.touches.length === 1) {
      e.preventDefault(); // Prevent page scroll during drag
      handleMove(e.touches[0].clientX, e.touches[0].clientY);
    }
  }, [handleMove]);

  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      if (onDragEnd) {
        onDragEnd(position);
      }
    }
  }, [isDragging, position, onDragEnd]);

  const handleTouchEnd = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      if (onDragEnd) {
        onDragEnd(position);
      }
    }
  }, [isDragging, position, onDragEnd]);

  useEffect(() => {
    if (isDragging) {
      // Mouse events
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      // Touch events
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
      document.addEventListener('touchcancel', handleTouchEnd);

      // Prevent text selection during drag
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'move';

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
        document.removeEventListener('touchcancel', handleTouchEnd);
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  // Check if element is interactive (should not trigger drag)
  const isInteractiveElement = useCallback((element) => {
    return (
      element.tagName === 'BUTTON' ||
      element.tagName === 'INPUT' ||
      element.closest('button') ||
      element.closest('input') ||
      element.closest('[role="button"]')
    );
  }, []);

  const handleMouseDown = useCallback((e) => {
    if (disabled) return;

    // Ignore if clicking on interactive elements or their children
    if (isInteractiveElement(e.target)) {
      return;
    }

    e.preventDefault();
    measureDragSize(e.currentTarget);
    setIsDragging(true);
    dragStartRef.current = { x: e.clientX, y: e.clientY };
    elementPositionRef.current = { ...position };
  }, [position, disabled, isInteractiveElement, measureDragSize]);

  const handleTouchStart = useCallback((e) => {
    if (disabled) return;

    // Ignore if touching on interactive elements
    if (isInteractiveElement(e.target)) {
      return;
    }

    // Only handle single finger touch
    if (e.touches.length !== 1) return;

    e.preventDefault();
    measureDragSize(e.currentTarget);
    setIsDragging(true);
    dragStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    elementPositionRef.current = { ...position };
  }, [position, disabled, isInteractiveElement, measureDragSize]);

  const resetPosition = useCallback(() => {
    setPosition(initialPosition);
  }, [initialPosition]);

  return {
    position,
    handleMouseDown,
    handleTouchStart,
    isDragging,
    resetPosition,
    setPosition, // Allow external position updates
  };
};
